import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny } from '../../api/client';
import {
  createApiKey,
  exportDevicesUrl,
  fetchApiKeys,
  fetchMetricCatalog,
  revokeApiKey,
} from '../../api/ops';
import { useSiteScope } from '../../app/scope';
import { useSites } from '../../data/hooks';
import { Card, Field, Modal } from '../../ui/components';
import { formatDateTime } from '../../ui/format';
import { useMe } from '../auth/session';

const PERMISSIONS: api.ApiKeyPermission[] = [
  'device.view',
  'telemetry.view',
  'alarm.view',
  'report.view',
  'asset.view',
  'group.view',
  'project.view',
  'telemetry.ingest',
];
const SENSOR_PREFIXES = ['environment.', 'traffic.', 'motion.', 'parking.'];

/**
 * Settings → Integrations: API keys for AMS / GIS / smart-city platforms and sensor networks, the OpenAPI description,
 * device exports (CSV / GeoJSON) and the metric catalogue external sensors use.
 */
export function IntegrationsSettings() {
  const { t } = useTranslation();
  const me = useMe().data;
  const manage = canAny(me, 'integration.manage');
  const siteId = useSiteScope((s) => s.siteId);
  return (
    <div className="ops-stack">
      {manage && <ApiKeysCard />}
      <Card title={t('opsSettings.integrations.api')}>
        <p className="muted" style={{ marginTop: 0 }}>
          {t('opsSettings.integrations.apiHint')}
        </p>
        <div className="toolbar">
          <a className="btn" href="/api/ext/v1/openapi.json" target="_blank" rel="noreferrer">
            OpenAPI 3.1
          </a>
          <a className="btn" href={exportDevicesUrl('geojson', siteId)} download>
            <Download size={15} /> {t('opsSettings.integrations.exportGeojson')}
          </a>
          <a className="btn" href={exportDevicesUrl('csv', siteId)} download>
            <Download size={15} /> {t('opsSettings.integrations.exportCsv')}
          </a>
        </div>
      </Card>
      <SensorsCard />
    </div>
  );
}

function ApiKeysCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: ['api-keys'], queryFn: fetchApiKeys });
  const [creating, setCreating] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
  return (
    <Card
      title={t('opsSettings.integrations.keys')}
      actions={
        <button type="button" className="btn btn-primary" onClick={() => (setToken(null), setCreating(true))}>
          <KeyRound size={15} /> {t('opsSettings.integrations.newKey')}
        </button>
      }
      bodyClass=""
    >
      {token && (
        <div className="card-body">
          <p className="notice">{t('opsSettings.integrations.tokenOnce')}</p>
          <div className="token-box">{token}</div>
        </div>
      )}
      <div className="ops-scroll">
        <table className="table table-wrap">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('common.site')}</th>
              <th>{t('opsSettings.integrations.permissions')}</th>
              <th>{t('opsSettings.integrations.lastUsed')}</th>
              <th>{t('common.status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(keys.data?.items ?? []).map((k) => (
              <tr key={k.id}>
                <td>
                  <strong>{k.name}</strong>
                  <span className="cell-sub mono">{k.token_prefix}…</span>
                </td>
                <td>{k.client_name ?? t('opsSettings.integrations.allClients')}</td>
                <td>
                  <div className="chips">
                    {k.permissions.map((p) => (
                      <span key={p} className="pill">
                        {p}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="muted">
                  {k.last_used_at ? formatDateTime(k.last_used_at) : t('common.never')}
                </td>
                <td>
                  {k.revoked_at ? (
                    <span className="pill">{t('opsSettings.integrations.revoked')}</span>
                  ) : k.expires_at && new Date(k.expires_at) < new Date() ? (
                    <span className="pill pill-warning">{t('opsSettings.integrations.expired')}</span>
                  ) : (
                    <span className="pill pill-online">{t('opsSettings.integrations.active')}</span>
                  )}
                </td>
                <td className="right">
                  {!k.revoked_at && k.can_manage && (
                    <button type="button" className="btn btn-danger" onClick={() => revoke.mutate(k.id)}>
                      {t('opsSettings.integrations.revoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {keys.data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  {t('opsSettings.integrations.noKeys')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {creating && (
        <NewKeyModal
          onClose={() => setCreating(false)}
          onCreated={(tok) => {
            setToken(tok);
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ['api-keys'] });
          }}
        />
      )}
    </Card>
  );
}

function NewKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (token: string) => void }) {
  const { t } = useTranslation();
  const me = useMe().data;
  const sites = useSites().data ?? [];
  const clients = sites.filter((s) => s.parentId === null);
  const globalAllowed = Boolean(
    me?.grants.some((g) => g.permission === 'integration.manage' && g.site_id === null),
  );
  const [name, setName] = useState('');
  const [client, setClient] = useState(globalAllowed ? '' : (clients[0]?.id ?? ''));
  const [perms, setPerms] = useState<api.ApiKeyPermission[]>(['device.view']);
  const [expires, setExpires] = useState('');
  const create = useMutation({
    mutationFn: () =>
      createApiKey({
        name,
        client_id: client || null,
        permissions: perms,
        expires_at: expires ? new Date(`${expires}T23:59:59`).toISOString() : null,
      }),
    onSuccess: (k) => onCreated(k.token),
  });
  return (
    <Modal title={t('opsSettings.integrations.newKey')} onClose={onClose}>
      <div className="form-grid">
        <Field label={t('common.name')}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </Field>
        <Field label={t('common.site')}>
          <select className="input" value={client} onChange={(e) => setClient(e.target.value)}>
            {globalAllowed && <option value="">{t('opsSettings.integrations.allClients')}</option>}
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('opsSettings.integrations.expires')} hint={t('opsSettings.integrations.expiresHint')}>
          <input className="input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </Field>
      </div>
      <fieldset className="fieldset">
        <legend>{t('opsSettings.integrations.permissions')}</legend>
        {PERMISSIONS.filter((p) => p !== 'telemetry.ingest' || client).map((p) => (
          <label key={p} className="check-row">
            <input
              type="checkbox"
              checked={perms.includes(p)}
              onChange={(e) => setPerms(e.target.checked ? [...perms, p] : perms.filter((x) => x !== p))}
            />
            <span className="mono">{p}</span>
            <span className="muted small">
              {t(
                `opsSettings.integrations.perm.${p.replace('.', '_')}` as 'opsSettings.integrations.perm.device_view',
              )}
            </span>
          </label>
        ))}
      </fieldset>
      {create.isError && (
        <p className="form-error">
          {t(`errors.${(create.error as Error).message}` as 'errors.generic', {
            defaultValue: (create.error as Error).message,
          })}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!name.trim() || perms.length === 0 || create.isPending}
          onClick={() => create.mutate()}
        >
          {t('common.create')}
        </button>
      </div>
    </Modal>
  );
}

function SensorsCard() {
  const { t } = useTranslation();
  const catalog = useQuery({
    queryKey: ['metrics', 'catalog'],
    queryFn: fetchMetricCatalog,
    staleTime: 10 * 60_000,
  });
  const sensors = (catalog.data?.items ?? []).filter((m) => SENSOR_PREFIXES.some((p) => m.key.startsWith(p)));
  const example = `curl -X POST -H "Authorization: Bearer <token>" -H "content-type: application/json" \\
  ${location.origin}/api/ext/v1/telemetry \\
  -d '{"device":{"external_id":"aq-7","name":"Air quality 7","lat":47.17,"lon":27.6},
       "metrics":{"environment.pm10_ugm3":42,"environment.temperature_c":12.5}}'`;
  return (
    <Card title={t('opsSettings.integrations.sensors')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('opsSettings.integrations.sensorsHint')}
      </p>
      <pre className="token-box" style={{ whiteSpace: 'pre-wrap' }}>
        {example}
      </pre>
      <div className="ops-scroll">
        <table className="table table-tight">
          <thead>
            <tr>
              <th>{t('opsSettings.integrations.metric')}</th>
              <th>{t('opsSettings.integrations.unit')}</th>
              <th>{t('common.name')}</th>
            </tr>
          </thead>
          <tbody>
            {sensors.map((m) => (
              <tr key={m.key}>
                <td className="mono">{m.key}</td>
                <td>{m.unit ?? '—'}</td>
                <td>
                  {t(`catalog.metric.${m.key}` as 'catalog.metric.electrical.power_w', {
                    defaultValue: m.name_en,
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
